import React from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area } from 'recharts';
import { CloudRain, Sun, Wind, Thermometer, CloudLightning, Umbrella } from 'lucide-react';
// Mock Data
const forecastData = Array.from({
  length: 24
}, (_, i) => ({
  time: `${i}:00`,
  gfs: 15 + Math.sin(i / 4) * 5 + Math.random(),
  ecmwf: 15 + Math.sin(i / 4) * 5 + Math.random() - 0.5,
  icon: 15 + Math.sin(i / 4) * 5 + Math.random() + 0.5,
  actual: i < 12 ? 15 + Math.sin(i / 4) * 5 : null
}));
const windEnsemble = Array.from({
  length: 24
}, (_, i) => ({
  time: `${i}:00`,
  p10: 5 + Math.random() * 2,
  p50: 8 + Math.random() * 2,
  p90: 12 + Math.random() * 2
}));
const CustomTooltip = ({
  active,
  payload,
  label
}: any) => {
  if (active && payload && payload.length) {
    return <div className="bg-white/95 backdrop-blur-xl border border-slate-200 rounded-xl p-3 shadow-xl z-50" data-id="element-3548">
        <p className="text-xs font-bold text-slate-900 mb-1" data-id="element-3549">{label}</p>
        {payload.map((entry: any) => <div key={entry.name} className="flex items-center gap-2 text-xs" data-id="element-3550">
            <div className="w-2 h-2 rounded-full" style={{
          backgroundColor: entry.color
        }} data-id="element-3551" />
            <span className="text-slate-600 capitalize" data-id="element-3552">{entry.name}:</span>
            <span className="font-mono font-medium" data-id="element-3553">
              {entry.value?.toFixed(1)}
            </span>
          </div>)}
      </div>;
  }
  return null;
};
export function WeatherImpactDashboard() {
  return <div className="min-h-screen p-6 space-y-6 pb-20 bg-sky-50/30" data-id="element-3554">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-3555">
        <div data-id="element-3556">
          <div className="flex items-center gap-2 mb-1" data-id="element-3557">
            <div className="p-2 bg-sky-500 rounded-lg" data-id="element-3558">
              <CloudRain size={20} className="text-white" data-id="element-3559" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" data-id="element-3560">
              Weather Impact
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-11" data-id="element-3561">
            Multi-model comparison and renewable generation impact
          </p>
        </div>

        <div className="flex gap-4" data-id="element-3562">
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200 shadow-sm" data-id="element-3563">
            <Thermometer size={16} className="text-rose-500" data-id="element-3564" />
            <span className="text-sm font-bold text-slate-700" data-id="element-3565">18.2°C</span>
          </div>
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200 shadow-sm" data-id="element-3566">
            <Wind size={16} className="text-sky-500" data-id="element-3567" />
            <span className="text-sm font-bold text-slate-700" data-id="element-3568">8.4 m/s</span>
          </div>
          <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200 shadow-sm" data-id="element-3569">
            <Sun size={16} className="text-amber-500" data-id="element-3570" />
            <span className="text-sm font-bold text-slate-700" data-id="element-3571">640 W/m²</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-3572">
        {/* Temperature Model Comparison */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-3573">
          <div className="flex justify-between items-center mb-6" data-id="element-3574">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-3575">
              Temperature Forecast Models
            </h3>
            <div className="flex gap-2" data-id="element-3576">
              <span className="text-xs px-2 py-1 bg-slate-100 rounded text-slate-600" data-id="element-3577">
                GFS
              </span>
              <span className="text-xs px-2 py-1 bg-slate-100 rounded text-slate-600" data-id="element-3578">
                ECMWF
              </span>
              <span className="text-xs px-2 py-1 bg-slate-100 rounded text-slate-600" data-id="element-3579">
                ICON
              </span>
            </div>
          </div>

          <div className="h-[300px] w-full" data-id="element-3580">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3581">
              <LineChart data={forecastData} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-3582">
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-3583" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} interval={3} data-id="element-3584" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} unit="°C" data-id="element-3585" />
                <Tooltip content={<CustomTooltip data-id="element-3587" />} data-id="element-3586" />
                <Legend data-id="element-3588" />
                <Line type="monotone" dataKey="gfs" name="GFS" stroke="#3b82f6" strokeWidth={2} dot={false} data-id="element-3589" />
                <Line type="monotone" dataKey="ecmwf" name="ECMWF" stroke="#10b981" strokeWidth={2} dot={false} data-id="element-3590" />
                <Line type="monotone" dataKey="icon" name="ICON" stroke="#f59e0b" strokeWidth={2} dot={false} data-id="element-3591" />
                <Line type="monotone" dataKey="actual" name="Actual" stroke="#0f172a" strokeWidth={3} dot={false} data-id="element-3592" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Wind Ensemble Spread */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-3593">
          <div className="flex justify-between items-center mb-6" data-id="element-3594">
            <h3 className="text-lg font-bold text-slate-900" data-id="element-3595">
              Wind Speed Uncertainty
            </h3>
            <span className="text-xs font-medium text-slate-500" data-id="element-3596">
              P10 - P90 Spread
            </span>
          </div>

          <div className="h-[300px] w-full" data-id="element-3597">
            <ResponsiveContainer width="100%" height="100%" data-id="element-3598">
              <AreaChart data={windEnsemble} margin={{
              top: 10,
              right: 10,
              left: 0,
              bottom: 0
            }} data-id="element-3599">
                <defs data-id="element-3600">
                  <linearGradient id="colorWind" x1="0" y1="0" x2="0" y2="1" data-id="element-3601">
                    <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} data-id="element-3602" />
                    <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0.1} data-id="element-3603" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" data-id="element-3604" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} interval={3} data-id="element-3605" />
                <YAxis axisLine={false} tickLine={false} tick={{
                fontSize: 12,
                fill: '#64748b'
              }} unit=" m/s" data-id="element-3606" />
                <Tooltip content={<CustomTooltip data-id="element-3608" />} data-id="element-3607" />
                <Area type="monotone" dataKey="p90" stackId="1" stroke="none" fill="#e0f2fe" data-id="element-3609" />
                <Area type="monotone" dataKey="p10" stackId="2" stroke="none" fill="#ffffff" data-id="element-3610" />
                <Line type="monotone" dataKey="p50" stroke="#0284c7" strokeWidth={2} dot={false} data-id="element-3611" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* Impact Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3612">
        {[{
        title: 'Solar Generation Impact',
        value: '-1.2 GW',
        desc: 'Due to cloud cover increase',
        colorClass: 'text-amber-600'
      }, {
        title: 'Heating Demand',
        value: '+450 MW',
        desc: 'Temp drop in evening',
        colorClass: 'text-rose-600'
      }, {
        title: 'Wind Ramp Risk',
        value: 'High',
        desc: 'Storm front approaching North',
        colorClass: 'text-sky-600'
      }].map((item, idx) => <motion.div key={item.title} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.2 + idx * 0.1
      }} className="bg-white/60 backdrop-blur-xl border border-slate-200/60 rounded-xl p-6 shadow-sm" data-id="element-3613">
            <h4 className="text-sm font-medium text-slate-500 mb-2" data-id="element-3614">
              {item.title}
            </h4>
            <div className={`text-2xl font-bold ${item.colorClass} mb-1`} data-id="element-3615">
              {item.value}
            </div>
            <p className="text-xs text-slate-400" data-id="element-3616">{item.desc}</p>
          </motion.div>)}
      </div>
    </div>;
}